// Copyright (c) Move Closer 2022.

import { Container } from '@movecloser/front-core'

import { logger } from '@core'

import { IPatch, IUpdater, IVersionResolver, PossibleVersion, VersionMode } from './contracts'
import { VersionResolver } from './version'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export class Updater implements IUpdater {
  protected applied = false
  protected appVersion: IVersionResolver
  protected storedVersion: PossibleVersion | null = null

  constructor (
    version: string,
    versionMode: VersionMode,
    protected container: Container,
    protected storageKey: string = 'x-core_version'
  ) {
    this.appVersion = new VersionResolver(version, versionMode)
    this.storedVersion = this.getStoredVersion()
  }

  /**
   * Responsible for execution all required patches.
   */
  public async applyPatches (patches: IPatch[]): Promise<void> {
    if (this.applied) {
      return
    }

    if (!this.storedVersion) {
      this.storedVersion = VersionResolver.deserialize(this.appVersion.serialize())
      this.storeCurrentVersion()
      return
    }

    const appliedPatches = []
    for (const patch of patches) {
      if (!patch.shouldApply(this.appVersion, this.storedVersion)) {
        continue
      }

      try {
        const result = patch.apply(this.container)
        if (result instanceof Promise) {
          await result
        }
      } catch (e) {
        logger('[Updater]: Cannot apply patch', 'error')
        logger(e, 'error')
      }

      appliedPatches.push(patch)
    }

    this.storeCurrentVersion()
    this.applied = true

    for (const patch of appliedPatches) {
      try {
        const result = patch.cleanup(this.container)
        if (result instanceof Promise) {
          await result
        }
      } catch (e) {
        logger('[Updater]: Cannot cleanup after patch', 'error')
        logger(e, 'error')
      }
    }
  }

  protected getStoredVersion (): PossibleVersion | null {
    if (typeof localStorage === 'undefined') {
      return null
    }

    const serializedVersion = localStorage.getItem(this.storageKey)
    if (!serializedVersion) {
      return null
    }

    return VersionResolver.deserialize(serializedVersion)
  }

  protected storeCurrentVersion (): void {
    if (typeof localStorage === 'undefined') {
      return
    }

    localStorage.setItem(this.storageKey, this.appVersion.serialize())
  }
}
